import { useState, useCallback, useEffect, useRef } from 'react';
import { useSessionContext } from '../contexts/SessionContext';
import { apiFetch } from '../lib/api';
import { useI18n } from '../i18n';
import type { PersonaEmotion } from '@roll1/shared';
import { PERSONA_EMOTIONS } from '@roll1/shared';

// --- Types ---

export type AiChatMode = 'draft' | 'auto' | 'suggest';
export type AiChatMessageRole = 'user' | 'assistant' | 'error' | 'tool' | 'thinking' | 'skill-plan';

export interface AiToolExecution {
  toolName: string;
  args: Record<string, unknown>;
  status: 'running' | 'done' | 'error';
  result?: string;
  success?: boolean;
}

export interface SkillPlanStep {
  id: string;
  label: string;
  type: string;
  details?: string;
  status: 'pending' | 'running' | 'done' | 'error';
}

export interface SkillPlanData {
  planId: string;
  skillId: string;
  title: string;
  steps: SkillPlanStep[];
  requiresApproval: boolean;
  decision: 'pending' | 'approved' | 'rejected' | 'auto';
  summary?: string;
  errorMessage?: string;
}

export interface AiChatMessage {
  id: string;
  role: AiChatMessageRole;
  content: string;
  toolData?: AiToolExecution;
  skillPlanData?: SkillPlanData;
  timestamp: string;
  emotion?: PersonaEmotion;
}

/** Strip [EMOTION]: ... line from streaming content (client-side complement to server parsing) */
const EMOTION_LINE_REGEX = /\n?\[EMOTION\]:\s*\w+\s*$/;

function stripEmotionLine(content: string): string {
  return content.replace(EMOTION_LINE_REGEX, '').trimEnd();
}

export interface AiConversationSummary {
  id: string;
  title: string | null;
  status: string;
  messageCount: number;
  totalTokensUsed: number;
  createdAt: string;
  updatedAt: string;
}

/** Base interface shared by GM and Player AI chat hooks */
export interface AiChatBase {
  messages: AiChatMessage[];
  isStreaming: boolean;
  error: string | null;
  conversationId: string | null;
  sendMessage: (text: string, previewMode?: boolean) => Promise<void>;
  resetChat: () => void;
  abortGeneration: () => void;
  isAborted: boolean;
  input: string;
  setInput: (text: string) => void;
  conversations: AiConversationSummary[];
  loadConversations: () => Promise<void>;
  loadConversation: (conversationId: string) => Promise<void>;
  isLoadingHistory: boolean;
  /** Last received emotion from ai:done (for header display) */
  lastEmotion: PersonaEmotion | undefined;
}

export interface UseAiChatReturn extends AiChatBase {
  mode: AiChatMode;
  setMode: (mode: AiChatMode) => void;
  canUndo: boolean;
  undoLastAction: () => Promise<void>;
  thinkingContent: string;
  approveSkillPlan: (planId: string, feedback?: string) => void;
  rejectSkillPlan: (planId: string, feedback?: string) => void;
}

// --- Constants ---

export const STREAMING_TIMEOUT_MS = 300_000;
// Generous cap so pasting a full character sheet / long prompt goes through
// (was 10k — too small for that). Still bounded to protect LLM context/cost.
export const MAX_MESSAGE_LENGTH = 50_000;

// --- Hook ---

export function useAiChat(): UseAiChatReturn {
  const { t } = useI18n();
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [mode, setMode] = useState<AiChatMode>('draft');
  const [lastUndoId, setLastUndoId] = useState<string | null>(null);
  const [thinkingContent, setThinkingContent] = useState('');
  const [isAborted, setIsAborted] = useState(false);
  const [input, setInput] = useState('');
  const [conversations, setConversations] = useState<AiConversationSummary[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const { mp } = useSessionContext();
  const sessionId = mp.session?.id;

  // Refs for use in WS handler closures
  const conversationIdRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isStreamingRef = useRef(false);
  const thinkingContentRef = useRef('');
  const pendingDeltaRef = useRef('');
  const rafIdRef = useRef<number>(0);

  // Sync refs with state
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);

  // --- Streaming timeout ---

  function clearStreamingTimeout() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }

  function resetStreamingTimeout() {
    clearStreamingTimeout();
    timeoutRef.current = setTimeout(() => {
      setIsStreaming(false);
      // Abort server-side stream to stop wasting resources/tokens
      if (conversationIdRef.current) {
        mp.send({ type: 'ai:abort', conversationId: conversationIdRef.current });
      }
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'error' as const,
        content: t('aiError.timeout'),
        timestamp: new Date().toISOString(),
      }]);
    }, STREAMING_TIMEOUT_MS);
  }

  // Cleanup timeout and RAF on unmount
  useEffect(() => {
    return () => {
      clearStreamingTimeout();
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  // --- WS event handlers ---

  function flushTextDelta(delta: string) {
    setMessages(prev => {
      const last = prev[prev.length - 1];
      if (last && last.role === 'assistant') {
        return [
          ...prev.slice(0, -1),
          { ...last, content: last.content + delta },
        ];
      }
      return [...prev, {
        id: crypto.randomUUID(),
        role: 'assistant' as const,
        content: delta,
        timestamp: new Date().toISOString(),
      }];
    });
  }

  function scheduleFlush() {
    if (rafIdRef.current) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = 0;
      const delta = pendingDeltaRef.current;
      if (!delta) return;
      pendingDeltaRef.current = '';
      flushTextDelta(delta);
    });
  }

  function handleToolStart(toolName: string, args: Record<string, unknown>, timestamp: string) {
    setMessages(prev => [...prev, {
      id: crypto.randomUUID(),
      role: 'tool' as const,
      content: '',
      toolData: { toolName, args, status: 'running' as const },
      timestamp,
    }]);
  }

  function handleToolResult(toolName: string, result: string, success: boolean) {
    setMessages(prev => {
      let idx = -1;
      for (let i = prev.length - 1; i >= 0; i--) {
        const m = prev[i];
        if (m.role === 'tool' && m.toolData?.toolName === toolName && m.toolData?.status === 'running') {
          idx = i;
          break;
        }
      }
      if (idx === -1) return prev;
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        toolData: {
          ...updated[idx].toolData!,
          status: success ? 'done' : 'error',
          result,
          success,
        },
      };
      return updated;
    });
  }

  function handleAiError(message: string, timestamp: string) {
    setIsStreaming(false);
    clearStreamingTimeout();
    setMessages(prev => [...prev, {
      id: crypto.randomUUID(),
      role: 'error' as const,
      content: message,
      timestamp,
    }]);
  }

  // --- WS subscription ---

  useEffect(() => {
    const unsub = mp.onMessage((msg) => {
      if (!msg.type.startsWith('ai:')) return;

      // Filter by conversationId if known
      if ('conversationId' in msg && conversationIdRef.current &&
          (msg as { conversationId: string }).conversationId !== conversationIdRef.current) return;

      resetStreamingTimeout();

      switch (msg.type) {
        case 'ai:thinking-delta':
          setThinkingContent(prev => {
            const next = prev + (msg as { delta: string }).delta;
            thinkingContentRef.current = next;
            return next;
          });
          break;
        case 'ai:text-delta':
          pendingDeltaRef.current += (msg as { delta: string }).delta;
          scheduleFlush();
          break;
        case 'ai:aborted': {
          if (pendingDeltaRef.current) {
            if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = 0; }
            flushTextDelta(pendingDeltaRef.current);
            pendingDeltaRef.current = '';
          }
          const wasStreaming = isStreamingRef.current;
          setIsStreaming(false);
          setIsAborted(true);
          clearStreamingTimeout();
          const abortedMsg = msg as { stage: string; thinkingSummary: string | null };
          const thinkingText = thinkingContentRef.current || abortedMsg.thinkingSummary || '';
          if (thinkingText) {
            setMessages(prev => [...prev, {
              id: crypto.randomUUID(),
              role: 'thinking' as const,
              content: thinkingText,
              timestamp: new Date().toISOString(),
            }]);
          }
          // Only show "stopped by GM" if streaming was still active (not already timed out)
          if (wasStreaming) {
            setMessages(prev => [...prev, {
              id: crypto.randomUUID(),
              role: 'error' as const,
              content: t('ai.stoppedByGM'),
              timestamp: new Date().toISOString(),
            }]);
          }
          break;
        }
        case 'ai:tool-start':
          handleToolStart(
            (msg as { toolName: string }).toolName,
            (msg as { args: Record<string, unknown> }).args,
            (msg as { timestamp: string }).timestamp,
          );
          break;
        case 'ai:tool-result':
          handleToolResult(
            (msg as { toolName: string }).toolName,
            (msg as { result: string }).result,
            (msg as { success: boolean }).success,
          );
          break;
        case 'ai:done': {
          if (pendingDeltaRef.current) {
            if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = 0; }
            flushTextDelta(pendingDeltaRef.current);
            pendingDeltaRef.current = '';
          }
          setIsStreaming(false);
          clearStreamingTimeout();
          if (thinkingContentRef.current) {
            setMessages(prev => [...prev, {
              id: crypto.randomUUID(),
              role: 'thinking' as const,
              content: thinkingContentRef.current,
              timestamp: new Date().toISOString(),
            }]);
          }

          // Extract emotion from ai:done and apply to last assistant message
          const rawEmotion = (msg as { emotion?: string }).emotion;
          const validEmotion = rawEmotion && PERSONA_EMOTIONS.includes(rawEmotion as PersonaEmotion)
            ? rawEmotion as PersonaEmotion
            : undefined;

          // Strip [EMOTION] line from last assistant message + set emotion
          setMessages(prev => {
            let lastIdx = -1;
            for (let i = prev.length - 1; i >= 0; i--) {
              if (prev[i].role === 'assistant') { lastIdx = i; break; }
            }
            if (lastIdx === -1) return prev;
            const updated = [...prev];
            updated[lastIdx] = {
              ...updated[lastIdx],
              content: stripEmotionLine(updated[lastIdx].content),
              emotion: validEmotion,
            };
            return updated;
          });

          break;
        }
        case 'ai:actions-completed':
          setLastUndoId((msg as { undoId: string }).undoId);
          break;
        case 'ai:undo-completed':
          setLastUndoId(null);
          break;
        case 'ai:error':
          handleAiError(
            (msg as { message: string }).message,
            (msg as { timestamp: string }).timestamp,
          );
          break;
        case 'ai:skill-plan': {
          const plan = msg as { conversationId: string; requiresApproval: boolean; plan: {
            planId: string; skillId: string; title: string;
            steps: Array<{ id: string; label: string; type: string; details?: string }>;
          }};
          setMessages(prev => [...prev, {
            id: crypto.randomUUID(),
            role: 'skill-plan' as const,
            content: '',
            skillPlanData: {
              planId: plan.plan.planId,
              skillId: plan.plan.skillId,
              title: plan.plan.title,
              steps: plan.plan.steps.map(s => ({ ...s, status: 'pending' as const })),
              requiresApproval: plan.requiresApproval,
              decision: plan.requiresApproval ? 'pending' : 'auto',
            },
            timestamp: new Date().toISOString(),
          }]);
          break;
        }
        case 'ai:skill-step': {
          const step = msg as { planId: string; stepId: string; status: 'running' | 'done' | 'error'; details?: string };
          setMessages(prev => prev.map(m => {
            if (m.role !== 'skill-plan' || m.skillPlanData?.planId !== step.planId) return m;
            return {
              ...m,
              skillPlanData: {
                ...m.skillPlanData!,
                steps: m.skillPlanData!.steps.map(s =>
                  s.id === step.stepId ? { ...s, status: step.status, details: step.details ?? s.details } : s
                ),
              },
            };
          }));
          break;
        }
        case 'ai:skill-complete': {
          const complete = msg as { planId: string; summary: string };
          setMessages(prev => prev.map(m => {
            if (m.role !== 'skill-plan' || m.skillPlanData?.planId !== complete.planId) return m;
            return {
              ...m,
              skillPlanData: {
                ...m.skillPlanData!,
                summary: complete.summary,
                steps: m.skillPlanData!.steps.map(s => s.status === 'pending' || s.status === 'running' ? { ...s, status: 'done' as const } : s),
              },
            };
          }));
          break;
        }
        case 'ai:skill-error': {
          const err = msg as { planId?: string; message: string };
          if (err.planId) {
            setMessages(prev => prev.map(m => {
              if (m.role !== 'skill-plan' || m.skillPlanData?.planId !== err.planId) return m;
              return {
                ...m,
                skillPlanData: {
                  ...m.skillPlanData!,
                  errorMessage: err.message,
                },
              };
            }));
          } else {
            handleAiError(err.message, new Date().toISOString());
          }
          break;
        }
      }
    });
    return unsub;
  }, [mp]);

  // --- sendMessage ---

  const sendMessage = useCallback(async (text: string, previewMode?: boolean) => {
    if (!sessionId || isStreamingRef.current) return;
    const trimmed = text.trim();
    if (!trimmed || trimmed.length > MAX_MESSAGE_LENGTH) return;

    const userMsg: AiChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: trimmed,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);
    setIsStreaming(true);
    setError(null);
    setThinkingContent('');
    thinkingContentRef.current = '';
    setIsAborted(false);

    try {
      const res = await apiFetch(`/api/sessions/${sessionId}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: trimmed,
          conversationId: conversationIdRef.current ?? undefined,
          mode,
          previewMode: previewMode ?? false,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        let errorMsg: string = t('aiError.sendError');
        if (res.status === 429) errorMsg = t('aiError.rateLimit');
        else if (res.status === 503) errorMsg = t('aiError.serviceUnavailable');
        else if (res.status === 403) errorMsg = t('aiError.gmOnly');
        else if (data.error) errorMsg = data.error;

        setMessages(prev => [...prev, {
          id: crypto.randomUUID(),
          role: 'error',
          content: errorMsg,
          timestamp: new Date().toISOString(),
        }]);
        setIsStreaming(false);
        return;
      }

      const { conversationId: newConvId } = await res.json();
      setConversationId(newConvId);
      resetStreamingTimeout();

    } catch {
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'error',
        content: t('aiError.networkError'),
        timestamp: new Date().toISOString(),
      }]);
      setIsStreaming(false);
    }
  }, [sessionId]);

  // --- resetChat ---

  const resetChat = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setIsStreaming(false);
    setError(null);
    setLastUndoId(null);
    setThinkingContent('');
    thinkingContentRef.current = '';
    setIsAborted(false);
    setInput('');
    clearStreamingTimeout();
  }, []);

  // --- abortGeneration ---

  const abortGeneration = useCallback(() => {
    if (!conversationIdRef.current || !isStreamingRef.current) return;
    mp.send({
      type: 'ai:abort',
      conversationId: conversationIdRef.current,
    });
    // Optimistic: immediately stop streaming UI so the button feels responsive.
    // Server will send ai:aborted later, but we update UI now.
    if (pendingDeltaRef.current) {
      if (rafIdRef.current) { cancelAnimationFrame(rafIdRef.current); rafIdRef.current = 0; }
      flushTextDelta(pendingDeltaRef.current);
      pendingDeltaRef.current = '';
    }
    setIsStreaming(false);
    setIsAborted(true);
    clearStreamingTimeout();
    if (thinkingContentRef.current) {
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'thinking' as const,
        content: thinkingContentRef.current,
        timestamp: new Date().toISOString(),
      }]);
      thinkingContentRef.current = '';
      setThinkingContent('');
    }
    setMessages(prev => [...prev, {
      id: crypto.randomUUID(),
      role: 'error' as const,
      content: t('ai.stoppedByGM'),
      timestamp: new Date().toISOString(),
    }]);
  }, [mp, t]);

  // --- undoLastAction ---

  const undoLastAction = useCallback(async () => {
    if (!sessionId || !lastUndoId || isStreamingRef.current) return;

    try {
      const res = await apiFetch(`/api/sessions/${sessionId}/ai/undo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ undoId: lastUndoId }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setLastUndoId(null);

        const errorMsg = data.code === 'AI_UNDO_SNAPSHOT_EXPIRED'
          ? t('aiError.undoExpired')
          : (data.error ?? t('aiError.sendError'));

        setMessages(prev => [...prev, {
          id: crypto.randomUUID(),
          role: 'error',
          content: errorMsg,
          timestamp: new Date().toISOString(),
        }]);
      }
      // setLastUndoId(null) is handled by WS ai:undo-completed
    } catch {
      setLastUndoId(null);
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'error',
        content: t('aiError.networkError'),
        timestamp: new Date().toISOString(),
      }]);
    }
  }, [sessionId, lastUndoId]);

  const canUndo = lastUndoId !== null && !isStreaming;

  // --- loadConversations ---

  const loadConversations = useCallback(async () => {
    if (!sessionId) return;
    setIsLoadingHistory(true);
    try {
      const res = await apiFetch(`/api/sessions/${sessionId}/ai/conversations?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations);
      }
    } catch { /* ignore */ }
    finally { setIsLoadingHistory(false); }
  }, [sessionId]);

  // --- loadConversation ---

  const loadConversation = useCallback(async (convId: string) => {
    if (!sessionId) return;
    setIsLoadingHistory(true);
    try {
      const res = await apiFetch(`/api/sessions/${sessionId}/ai/conversations/${convId}`);
      if (res.ok) {
        const data = await res.json();
        setConversationId(convId);
        // Map server messages to client AiChatMessage format
        const clientMessages: AiChatMessage[] = data.messages
          .filter((m: { content: unknown }) => typeof m.content === 'string')
          .map((m: { role: string; content: string; timestamp: string }) => ({
            id: crypto.randomUUID(),
            role: m.role === 'assistant' ? 'assistant' as const : 'user' as const,
            content: m.content,
            timestamp: m.timestamp,
          }));
        setMessages(clientMessages);
        setInput('');
        setLastUndoId(null);
        setThinkingContent('');
      }
    } catch { /* ignore */ }
    finally { setIsLoadingHistory(false); }
  }, [sessionId]);

  const approveSkillPlan = useCallback((planId: string, feedback?: string) => {
    if (!conversationIdRef.current) return;
    mp.send({ type: 'ai:plan-approve', conversationId: conversationIdRef.current, planId, feedback });
    setMessages(prev => prev.map(m => {
      if (m.role !== 'skill-plan' || m.skillPlanData?.planId !== planId) return m;
      return { ...m, skillPlanData: { ...m.skillPlanData!, decision: 'approved' as const } };
    }));
  }, [mp]);

  const rejectSkillPlan = useCallback((planId: string, feedback?: string) => {
    if (!conversationIdRef.current) return;
    mp.send({ type: 'ai:plan-reject', conversationId: conversationIdRef.current, planId, feedback });
    setMessages(prev => prev.map(m => {
      if (m.role !== 'skill-plan' || m.skillPlanData?.planId !== planId) return m;
      return { ...m, skillPlanData: { ...m.skillPlanData!, decision: 'rejected' as const } };
    }));
  }, [mp]);

  // Derive lastEmotion from messages
  let lastEmotion: PersonaEmotion | undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant' && messages[i].emotion) {
      lastEmotion = messages[i].emotion;
      break;
    }
  }

  return {
    messages, isStreaming, error, conversationId, sendMessage, resetChat,
    mode, setMode, canUndo, undoLastAction, thinkingContent, abortGeneration,
    isAborted, input, setInput, conversations, loadConversations, loadConversation,
    isLoadingHistory, approveSkillPlan, rejectSkillPlan, lastEmotion,
  };
}
